"use client";
import { useState, useEffect } from "react";
import api from "@/lib/axios";

/** Dikirim (window event) setelah setting yang memengaruhi daftar menu disimpan (mis. saklar Serial Number). */
export const MENUS_CHANGED_EVENT = "mgtruck:menus-changed";

interface MenuPermissions {
    isAdmin: boolean;
    /** Set KdMMenu yang user boleh baca (dari /system/level-manager/my-permissions).
     *  `null` = belum ke-fetch. Konsumen (Sidebar) memilih behavior fail-open/closed. */
    allowedCodes: Set<string> | null;
    /** Set KdMMenu yang IsShow=1 di mgsymmenu (dari /menus).
     *  `null` = belum ke-fetch. Admin biasanya bypass filter ini.
     *  Adopsi Fase 1 sidebar MGMX (cowork/docs/ADOPSI-MGMX-SIDEBAR-PLAN.md). */
    visibleCodes: Set<string> | null;
}

/**
 * Sumber izin visibilitas menu sidebar (kombinasi):
 *  1. Global filter: `mgsymmenu.IsShow` → `visibleCodes` (dari GET /api/menus)
 *  2. Per-user role: `mgsytlevelmanager.CanRead` → `allowedCodes` (dari GET /api/system/level-manager/my-permissions)
 *
 * Sidebar melakukan AND dari kedua filter tsb (admin bypass filter #1 sesuai pola MGMX).
 *
 * Perilaku fetch:
 *  - Kedua endpoint di-fetch PARALEL saat mount.
 *  - Cache sessionStorage per-user key `_menuPermsU_${userId}` (allowedCodes only — visibleCodes
 *    tidak di-cache karena globally-shared & sudah ringan).
 *  - FAIL-SAFE saat error: sembunyikan menu ter-gate (Set kosong), BUKAN fail-open.
 */
export function useMenuPermissions(): MenuPermissions {
    const [isAdmin, setIsAdmin] = useState(false);
    const [allowedCodes, setAllowedCodes] = useState<Set<string> | null>(null);
    const [visibleCodes, setVisibleCodes] = useState<Set<string> | null>(null);

    useEffect(() => {
        if (typeof window === "undefined") return;

        const token = localStorage.getItem("token");
        if (!token) {
            setAllowedCodes(new Set()); // tak login → tak ada menu ter-gate
            setVisibleCodes(new Set());
            return;
        }

        let userId = "";
        let level: number | undefined;
        try {
            const u = JSON.parse(localStorage.getItem("user") || "{}");
            userId = String(u.id ?? "");
            level = u.level;
        } catch {
            /* user corrupt → tetap fetch */
        }

        // Fetch #2 (paralel): /menus — IsShow=1 KdMMenu list. Berlaku untuk semua user
        // (admin juga fetch supaya konsumen tetap konsisten walau admin bypass filter ini).
        const loadVisible = () => api.get<{ success?: boolean; data?: Array<{ KdMMenu?: string | null }> }>("/menus")
            .then((res) => {
                const codes = (res.data.data ?? [])
                    .map((r) => r.KdMMenu)
                    .filter((c): c is string => typeof c === "string" && c.length > 0);
                setVisibleCodes(new Set(codes));
            })
            .catch(() => {
                // FAIL-SAFE: kalau /menus gagal, jangan blok semua menu (bisa akibatkan
                // sidebar kosong permanen). Set null di-treat sbg "belum ready" oleh sidebar.
                // Kalau error persisten, admin harus fix BE, bukan block user.
                setVisibleCodes(new Set());
            });
        loadVisible();

        // Fast-path admin (level 1) — lihat semua permission-wise.
        if (level === 1) {
            setIsAdmin(true);
            setAllowedCodes(new Set());
            return;
        }

        // Cache sesi per-USER (bukan per level) supaya tak bocor antar user.
        const cacheKey = `_menuPermsU_${userId}`;
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
            try {
                const p = JSON.parse(cached) as { isAdmin: boolean; codes: string[] };
                setIsAdmin(!!p.isAdmin);
                setAllowedCodes(new Set(p.codes || []));
                return;
            } catch {
                /* cache korup → fetch ulang */
            }
        }

        api.get<{ isAdmin?: boolean; canReadMenus?: string[] }>("/system/level-manager/my-permissions")
            .then((res) => {
                const admin = !!res.data.isAdmin;
                const codes = res.data.canReadMenus ?? [];
                setIsAdmin(admin);
                setAllowedCodes(new Set(codes));
                try {
                    sessionStorage.setItem(cacheKey, JSON.stringify({ isAdmin: admin, codes }));
                } catch {
                    /* quota */
                }
            })
            .catch(() => {
                // FAIL-SAFE: jangan tampilkan semua menu saat backend error.
                // Sembunyikan menu ter-gate (Set kosong); menu tanpa kdMenu tetap tampil.
                setIsAdmin(false);
                setAllowedCodes(new Set());
            });
    }, []);

    // Global Information disimpan (saklar Serial Number berubah) → muat ulang daftar menu tanpa login ulang.
    // Gagal muat → pertahankan daftar yang sudah ada (jangan mengosongkan sidebar).
    useEffect(() => {
        const onMenusChanged = () => {
            api.get<{ data?: Array<{ KdMMenu?: string | null }> }>("/menus")
                .then((res) => {
                    const codes = (res.data.data ?? [])
                        .map((r) => r.KdMMenu)
                        .filter((c): c is string => typeof c === "string" && c.length > 0);
                    setVisibleCodes(new Set(codes));
                })
                .catch(() => undefined);
        };
        window.addEventListener(MENUS_CHANGED_EVENT, onMenusChanged);
        return () => window.removeEventListener(MENUS_CHANGED_EVENT, onMenusChanged);
    }, []);

    return { isAdmin, allowedCodes, visibleCodes };
}
